import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shapes } from './Shapes';
import { Check, RefreshCw, Star, Info, MousePointer2, HelpCircle } from 'lucide-react';
import confetti from 'canvas-confetti';
import Quiz from './Quiz';
import { useAuth } from '../../context/AuthContext';

import { sounds } from '../../utils/SoundManager';

const Section = ({ title, children, className, id }) => (
    <section id={id} className={`bg-white rounded-3xl p-6 shadow-xl border-4 border-brand-blue/10 ${className}`}>
        <h2 className="text-2xl md:text-3xl font-bold text-brand-purple mb-6 font-hand">{title}</h2>
        {children}
    </section>
);

const ShapeMatchingGame = () => {
    const [selectedShape, setSelectedShape] = useState(null);
    const [selectedName, setSelectedName] = useState(null);
    const [matches, setMatches] = useState([]); // Array of ids
    const [wrongMatch, setWrongMatch] = useState(null); // {shapeId, nameId}

    const data = [
        { id: 'tri', name: 'Triángulo', Icon: Shapes.Triangle, color: 'text-brand-blue' },
        { id: 'cua', name: 'Cuadrilátero', Icon: Shapes.Quadrilateral, color: 'text-brand-orange' },
        { id: 'pen', name: 'Pentágono', Icon: Shapes.Pentagon, color: 'text-brand-purple' },
        { id: 'hex', name: 'Hexágono', Icon: Shapes.Hexagon, color: 'text-brand-blue' },
        { id: 'rhom', name: 'Rombo', Icon: Shapes.Rhombus, color: 'text-cyan-500' },
        { id: 'trap', name: 'Trapecio', Icon: Shapes.Trapezoid, color: 'text-brand-orange' },
    ];

    // Shuffled names for the right column
    const [shuffledNames] = useState(() => [...data].sort(() => Math.random() - 0.5));

    const handleShapeClick = (id) => {
        if (matches.includes(id)) return;
        sounds.playPop();
        setSelectedShape(id);
        if (selectedName) {
            checkMatch(id, selectedName);
        }
    };

    const handleNameClick = (id) => {
        if (matches.includes(id)) return;
        sounds.playPop();
        setSelectedName(id);
        if (selectedShape) {
            checkMatch(selectedShape, id);
        }
    };

    const checkMatch = (sId, nId) => {
        if (sId === nId) {
            setMatches(prev => [...prev, sId]);
            sounds.playCorrect();
            setSelectedShape(null);
            setSelectedName(null);
            if (matches.length + 1 === data.length) {
                confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
            }
        } else {
            setWrongMatch({ sId, nId });
            sounds.playIncorrect();
            setTimeout(() => {
                setWrongMatch(null);
                setSelectedShape(null);
                setSelectedName(null);
            }, 600);
        }
    };

    const reset = () => {
        sounds.playAction();
        setMatches([]);
        setSelectedShape(null);
        setSelectedName(null);
    };

    return (
        <div className="space-y-8">
            <div className="grid grid-cols-2 gap-12 max-w-2xl mx-auto items-start">
                {/* Column 1: Shapes */}
                <div className="space-y-4">
                    <h4 className="text-center font-bold text-gray-400 uppercase text-xs tracking-widest mb-4">Figura</h4>
                    {data.map((item) => (
                        <motion.button
                            key={item.id}
                            whileHover={{ scale: matches.includes(item.id) ? 1 : 1.05 }}
                            whileTap={{ scale: matches.includes(item.id) ? 1 : 0.95 }}
                            onClick={() => handleShapeClick(item.id)}
                            className={`
                                w-full h-24 p-4 rounded-2xl border-4 transition-all flex items-center justify-center
                                ${matches.includes(item.id) ? 'bg-green-50 border-green-500 opacity-50' :
                                    selectedShape === item.id ? 'border-brand-purple bg-purple-50 shadow-inner' :
                                        wrongMatch?.sId === item.id ? 'border-red-500 bg-red-50' : 'bg-white border-gray-100 hover:border-brand-purple/30'}
                            `}
                        >
                            <div className={`w-16 h-16 ${item.color}`}>
                                <item.Icon fill="currentColor" opacity="0.2" />
                            </div>
                        </motion.button>
                    ))}
                </div>

                {/* Column 2: Names */}
                <div className="space-y-4">
                    <h4 className="text-center font-bold text-gray-400 uppercase text-xs tracking-widest mb-4">Nombre</h4>
                    {shuffledNames.map((item) => (
                        <motion.button
                            key={item.id}
                            whileHover={{ scale: matches.includes(item.id) ? 1 : 1.05 }}
                            whileTap={{ scale: matches.includes(item.id) ? 1 : 0.95 }}
                            onClick={() => handleNameClick(item.id)}
                            className={`
                                w-full h-24 p-4 rounded-2xl border-4 transition-all flex items-center justify-center
                                font-bold text-lg font-hand
                                ${matches.includes(item.id) ? 'bg-green-50 border-green-500 text-green-700 opacity-50' :
                                    selectedName === item.id ? 'border-brand-purple bg-purple-50 text-brand-purple' :
                                        wrongMatch?.nId === item.id ? 'border-red-500 bg-red-50 text-red-700' : 'bg-white border-gray-100 hover:border-brand-purple/30 text-gray-600'}
                            `}
                        >
                            {item.name}
                        </motion.button>
                    ))}
                </div>
            </div>

            <div className="flex justify-center">
                <button
                    onClick={reset}
                    className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-500 rounded-full font-bold hover:bg-gray-200 transition-colors"
                >
                    <RefreshCw size={20} /> Reiniciar Juego
                </button>
            </div>
        </div>
    );
};

const Polygons = () => {
    const { saveProgress } = useAuth();
    const [counts, setCounts] = useState({ shape1: '', shape2: '', shape3: '', shape4: '', shape5: '', shape6: '' });
    const [feedback, setFeedback] = useState({});

    const handleCountChange = (id, val) => {
        setCounts(prev => ({ ...prev, [id]: val }));
    };

    const checkCount = (id, correct) => {
        const isCorrect = parseInt(counts[id]) === correct;
        setFeedback(prev => ({ ...prev, [id]: isCorrect ? 'correct' : 'incorrect' }));
        if (isCorrect) {
            sounds.playCorrect();
            confetti({ particleCount: 30, spread: 50, origin: { y: 0.8 } });
        } else {
            sounds.playIncorrect();
        }
    };

    const quizQuestions = [
        {
            question: "¿Cómo definimos un polígono?",
            options: [
                "Una línea curva abierta",
                "Una figura plana limitada por una línea poligonal cerrada",
                "Cualquier dibujo hecho con regla"
            ],
            correct: 1
        },
        {
            question: "¿Qué es un lado en un polígono?",
            options: [
                "Cualquier línea del dibujo",
                "La línea recta que une dos vértices consecutivos",
                "El punto donde se juntan dos líneas"
            ],
            correct: 1
        },
        {
            question: "Este polígono IRREGULAR tiene 4 lados. ¿Cómo lo llamamos?",
            visual: <div className="w-24 h-24 mx-auto"><Shapes.IrregularQuadrilateral fill="#F7258520" /></div>,
            options: ["Triángulo", "Pentágono", "Cuadrilátero"],
            correct: 2
        },
        {
            question: "¿Cómo se llaman los puntos rojos señalados en este pentágono?",
            visual: (
                <div className="w-32 h-32 mx-auto relative">
                    <Shapes.Pentagon stroke="#ccc" />
                    <div className="absolute top-[15%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-red-500 rounded-full" />
                    <div className="absolute top-[41%] left-[85%] -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-red-500 rounded-full" />
                </div>
            ),
            options: ["Lados", "Vértices", "Curvas"],
            correct: 1
        },
        {
            question: "Si un polígono tiene 6 lados, ¿cuántas 'esquinas' o vértices tiene?",
            visual: <div className="w-24 h-24 mx-auto"><Shapes.Hexagon fill="#4CC9F020" /></div>,
            options: ["5 vértices", "6 vértices", "4 vértices"],
            correct: 1
        },
        {
            question: "¿Qué tipo de polígono es esta figura irregular de 5 lados?",
            visual: <div className="w-24 h-24 mx-auto"><Shapes.IrregularPentagon fill="#7209B720" /></div>,
            options: ["Pentágono", "Hexágono", "Cuadrilátero"],
            correct: 0
        },
        {
            question: "¡Verdadero o Falso! Un polígono puede estar formado por líneas CURVAS.",
            options: ["Verdadero", "Falso (Solo líneas poligonales rectas)"],
            correct: 1
        }
    ];

    const handleQuizComplete = (score) => {
        saveProgress('unit2', score);
    };

    return (
        <div className="space-y-12 pb-20">
            {/* Header */}
            <header className="text-center space-y-2">
                <span className="bg-brand-orange text-white px-4 py-1 rounded-full font-bold text-sm tracking-wide uppercase">
                    Unidad 2
                </span>
                <h1 className="text-4xl font-extrabold text-gray-800 font-hand">Reconozco polígonos</h1>
                <p className="text-xl text-gray-500 mb-4">Identificamos las figuras según su forma.</p>
            </header>

            {/* Theory: What is a polygon? */}
            <Section title="¿Qué es un polígono?">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-dashed border-blue-200 mb-8 text-center flex flex-col md:flex-row items-center gap-6">
                    <div className="flex-1 text-lg text-gray-700 leading-relaxed">
                        Un <span className="font-bold text-brand-blue">polígono</span> es una figura plana limitada por una línea poligonal <span className="font-bold text-brand-purple italic">cerrada</span>.
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                        <div className="w-16 h-16 mx-auto"><Shapes.Rhombus fill="#4CC9F020" /></div>
                        <div className="w-16 h-16 mx-auto"><Shapes.Triangle fill="#4361EE20" /></div>
                        <div className="w-16 h-16 mx-auto"><Shapes.Trapezoid fill="#FF9F1C20" /></div>
                        <div className="w-16 h-16 mx-auto"><Shapes.Irregular fill="#70E00020" /></div>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div className="space-y-4">
                        <div className="bg-white p-6 rounded-3xl shadow-lg border-2 border-gray-100 flex items-center gap-4">
                            <div className="bg-red-100 p-3 rounded-2xl text-red-500">
                                <Info size={24} />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-800">Vértices</h4>
                                <p className="text-sm text-gray-500 text-pretty">Son las "esquinas" donde se unen los lados.</p>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-3xl shadow-lg border-2 border-gray-100 flex items-center gap-4">
                            <div className="bg-blue-100 p-3 rounded-2xl text-blue-500">
                                <MousePointer2 size={24} />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-800">Lados</h4>
                                <p className="text-sm text-gray-500 text-pretty">Es la línea recta que une dos vértices consecutivos.</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-slate-50 p-6 rounded-3xl border-2 border-slate-100 relative overflow-hidden group">
                        <div className="text-center mb-4 text-xs font-bold uppercase tracking-widest text-slate-400">Ejemplo: Pentágono</div>
                        <div className="w-48 h-48 mx-auto relative">
                            <Shapes.Pentagon stroke="#4CC9F0" fill="#4CC9F010" />

                            {/* Vértice markers (exact matching SVG points) */}
                            {/* Point 1: 50,15 */}
                            <div className="absolute top-[15%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-sm z-10 animate-pulse" />
                            {/* Point 2: 85,41 */}
                            <div className="absolute top-[41%] left-[85%] -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-sm z-10 animate-pulse" />
                            {/* Point 3: 71,83 */}
                            <div className="absolute top-[83%] left-[71%] -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-sm z-10 animate-pulse" />
                            {/* Point 4: 29,83 */}
                            <div className="absolute top-[83%] left-[29%] -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-sm z-10 animate-pulse" />
                            {/* Point 5: 15,41 */}
                            <div className="absolute top-[41%] left-[15%] -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-sm z-10 animate-pulse" />

                            {/* Label for vertex - Positioned near Point 2 (85,41) */}
                            <div className="absolute top-[34%] left-[89%] text-red-600 font-bold font-hand text-lg z-20 pointer-events-none whitespace-nowrap">
                                ← vértice
                            </div>

                            {/* Label for side */}
                            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-blue-600 font-bold font-hand text-xl bg-white/80 px-3 py-1 rounded-lg shadow-sm border border-blue-100 z-20 whitespace-nowrap">
                                lados
                            </div>
                        </div>
                        <p className="text-center mt-6 text-gray-600 font-medium">Este polígono tiene <u className="text-red-500">5 vértices</u> y <u className="text-blue-500">5 lados</u>.</p>
                    </div>
                </div>
            </Section>

            {/* Panel de Clasificación con Polígonos Irregulares */}
            <Section title="¿Cómo se llaman los polígonos?">
                <p className="text-gray-500 mb-8 italic">Según el número de lados, los polígonos tienen nombres diferentes:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-3xl border-2 border-blue-100 flex flex-col items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
                        <div className="w-24 h-24"><Shapes.IrregularTriangle fill="#4361EE20" /></div>
                        <div className="text-center">
                            <h4 className="font-bold text-brand-blue text-lg italic">Triángulo</h4>
                            <p className="text-sm text-gray-600">Tiene <span className="font-bold">3 lados</span>.</p>
                        </div>
                    </div>
                    <div className="bg-gradient-to-br from-pink-50 to-white p-6 rounded-3xl border-2 border-pink-100 flex flex-col items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
                        <div className="w-24 h-24"><Shapes.IrregularQuadrilateral fill="#F7258520" /></div>
                        <div className="text-center">
                            <h4 className="font-bold text-brand-orange text-lg italic uppercase tracking-tight">Cuadrilátero</h4>
                            <p className="text-sm text-gray-600">Tiene <span className="font-bold">4 lados</span>.</p>
                        </div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-3xl border-2 border-purple-100 flex flex-col items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
                        <div className="w-24 h-24"><Shapes.IrregularPentagon fill="#7209B720" /></div>
                        <div className="text-center">
                            <h4 className="font-bold text-brand-purple text-lg italic capitalize">Pentágono</h4>
                            <p className="text-sm text-gray-600">Tiene <span className="font-bold">5 lados</span>.</p>
                        </div>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-50 to-white p-6 rounded-3xl border-2 border-cyan-100 flex flex-col items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
                        <div className="w-24 h-24"><Shapes.IrregularHexagon fill="#4CC9F020" /></div>
                        <div className="text-center">
                            <h4 className="font-bold text-brand-blue text-lg italic">Hexágono</h4>
                            <p className="text-sm text-gray-600">Tiene <span className="font-bold">6 lados</span>.</p>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Activity 1: Count sides and vertices */}
            <Section title="1. Repasa y completa">
                <p className="text-gray-500 mb-8 italic">¿Cuántos lados y vértices tienen estas figuras?</p>
                <div className="grid md:grid-cols-2 gap-12">
                    {/* Triangle */}
                    <div className="bg-gray-50 p-8 rounded-3xl flex flex-col md:flex-row items-center gap-8">
                        <div className="w-40 h-40"><Shapes.Triangle stroke="#94a3b8" /></div>
                        <div className="space-y-4 flex-1">
                            <div className="flex items-center gap-3">
                                <label className="text-lg font-bold text-gray-700">Lados:</label>
                                <input
                                    type="number"
                                    className={`w-16 p-2 rounded-xl border-2 text-center text-xl font-bold ${feedback.shape1 === 'correct' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                                    value={counts.shape1}
                                    onChange={(e) => handleCountChange('shape1', e.target.value)}
                                    onBlur={() => checkCount('shape1', 3)}
                                />
                                {feedback.shape1 === 'correct' && <Check className="text-green-500" />}
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-lg font-bold text-gray-700">Vértices:</label>
                                <input
                                    type="number"
                                    className={`w-16 p-2 rounded-xl border-2 text-center text-xl font-bold ${feedback.shape2 === 'correct' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                                    value={counts.shape2}
                                    onChange={(e) => handleCountChange('shape2', e.target.value)}
                                    onBlur={() => checkCount('shape2', 3)}
                                />
                                {feedback.shape2 === 'correct' && <Check className="text-green-500" />}
                            </div>
                        </div>
                    </div>

                    {/* Trapezoid */}
                    <div className="bg-gray-50 p-8 rounded-3xl flex flex-col md:flex-row items-center gap-8">
                        <div className="w-40 h-40"><Shapes.Trapezoid stroke="#94a3b8" /></div>
                        <div className="space-y-4 flex-1">
                            <div className="flex items-center gap-3">
                                <label className="text-lg font-bold text-gray-700">Lados:</label>
                                <input
                                    type="number"
                                    className={`w-16 p-2 rounded-xl border-2 text-center text-xl font-bold ${feedback.shape3 === 'correct' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                                    value={counts.shape3}
                                    onChange={(e) => handleCountChange('shape3', e.target.value)}
                                    onBlur={() => checkCount('shape3', 4)}
                                />
                                {feedback.shape3 === 'correct' && <Check className="text-green-500" />}
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-lg font-bold text-gray-700">Vértices:</label>
                                <input
                                    type="number"
                                    className={`w-16 p-2 rounded-xl border-2 text-center text-xl font-bold ${feedback.shape4 === 'correct' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                                    value={counts.shape4}
                                    onChange={(e) => handleCountChange('shape4', e.target.value)}
                                    onBlur={() => checkCount('shape4', 4)}
                                />
                                {feedback.shape4 === 'correct' && <Check className="text-green-500" />}
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Activity 2: Polygon Classification */}
            <Section title="2. ¿Cuántos lados tienen?">
                <p className="text-gray-500 mb-8 italic">Escribe el número de lados y aprende el nombre de cada polígono.</p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                        { id: 'shape5', name: 'TRIÁNGULO', sides: 3, icon: <Shapes.Triangle fill="#4361EE20" /> },
                        { id: 'shape6', name: 'CUADRILÁTERO', sides: 4, icon: <Shapes.Quadrilateral fill="#F7258520" /> },
                        { id: 'shape7', name: 'PENTÁGONO', sides: 5, icon: <Shapes.Pentagon fill="#7209B720" /> },
                        { id: 'shape8', name: 'HEXÁGONO', sides: 6, icon: <Shapes.Hexagon fill="#4CC9F020" /> },
                    ].map((item) => (
                        <div key={item.id} className="bg-white p-6 rounded-3xl shadow-md border border-gray-100 flex flex-col items-center gap-4 group hover:shadow-xl transition-shadow text-center">
                            <div className="bg-slate-100 px-3 py-1 rounded-full text-[10px] font-bold text-slate-500 tracking-widest">{item.name}</div>
                            <div className="w-24 h-24 group-hover:scale-110 transition-transform">{item.icon}</div>
                            <div className="flex items-center gap-2 mt-auto">
                                <span className="text-sm font-bold text-gray-400 uppercase">Lados:</span>
                                <input
                                    type="number"
                                    className={`w-12 p-1 rounded-lg border-2 text-center font-bold ${feedback[item.id] === 'correct' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200'}`}
                                    onChange={(e) => {
                                        if (parseInt(e.target.value) === item.sides) {
                                            setFeedback(prev => ({ ...prev, [item.id]: 'correct' }));
                                            sounds.playCorrect();
                                            confetti({ particleCount: 20, spread: 30, origin: { y: 0.8 } });
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Activity 3: Shape Matching Game */}
            <Section title="3. ¡Juego de Emparejar!" className="bg-purple-50/50">
                <p className="text-gray-500 mb-8 italic">Une cada figura con su nombre correcto.</p>
                <ShapeMatchingGame />
            </Section>

            {/* Quiz Section */}
            <div className="py-8">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-brand-purple mb-2">🧠 ¡Comprueba lo que sabes!</h2>
                    <p className="text-gray-500">Responde a estas preguntas para completar la unidad.</p>
                </div>
                <div className="max-w-2xl mx-auto">
                    <Quiz questions={quizQuestions} onComplete={handleQuizComplete} />
                </div>
            </div>
        </div>
    );
};

export default Polygons;
